import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";

import type {
  AssetCommentSubjectInput,
  AssetCommentThreadsBoolExp,
} from "src/graphql";

/**
 * Maps a comment thread's subject onto the two shapes GraphQL wants it in.
 *
 * The union itself is the backend's, which in turn mirrors the
 * `asset_comment_threads_one_subject` CHECK — so an arm added for LCM emails or
 * campaign artifacts is a type error in both mappers below until it is handled.
 */

/**
 * The `where` predicate selecting one subject's threads.
 *
 * Reads pass the whole predicate rather than named columns so a single query
 * serves every subject. The ad arm can't be expressed as a bare id pair: the
 * primary size stores a null `channel_size_id`, and `_eq: null` matches nothing
 * in SQL, so that case has to become `_is_null`.
 */
export function toSubjectFilter(
  subject: CommentThreadSubject,
): AssetCommentThreadsBoolExp {
  switch (subject.kind) {
    case "adBriefAsset":
      return {
        ad_brief_asset_id: { _eq: subject.adBriefAssetId },
        channel_size_id:
          subject.channelSizeId === null
            ? { _is_null: true }
            : { _eq: subject.channelSizeId },
      };
    case "contentEmail":
      return { content_email_id: { _eq: subject.contentEmailId } };
    case "campaignArtifact":
      return { campaign_artifact_id: { _eq: subject.campaignArtifactId } };
    case "ampProject":
      return { amp_project_id: { _eq: subject.ampProjectId } };
    case "ampTask":
      return { amp_task_id: { _eq: subject.ampTaskId } };
    case "settingsEntity":
      return { settings_entity_id: { _eq: subject.settingsEntityId } };
    case "ampCanvas":
      return { amp_canvas_id: { _eq: subject.ampCanvasId } };
  }
}

/** Maps a subject onto the create-thread mutation's one-of input. */
export function toSubjectInput(
  subject: CommentThreadSubject,
): AssetCommentSubjectInput {
  switch (subject.kind) {
    case "adBriefAsset":
      return {
        adBriefAssetId: subject.adBriefAssetId,
        channelSizeId: subject.channelSizeId,
      };
    case "contentEmail":
      return { contentEmailId: subject.contentEmailId };
    case "campaignArtifact":
      return { campaignArtifactId: subject.campaignArtifactId };
    case "ampProject":
      return { ampProjectId: subject.ampProjectId };
    case "ampTask":
      return { ampTaskId: subject.ampTaskId };
    case "settingsEntity":
      return { settingsEntityId: subject.settingsEntityId };
    case "ampCanvas":
      return { ampCanvasId: subject.ampCanvasId };
  }
}
